import { Link } from "react-router-dom";
import { Banknote, MessageCircle } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { useCategories } from "../../features/catalog/queries";
import { useStoreConfig } from "../../features/config/queries";

// Trailing links that aren't product categories, so they're not managed via
// Admin → Categories — always shown after whatever categories are active there.
const STATIC_SHOP_LINKS = [
  { to: "/bundles", label: "Bundles" },
  { to: "/offers", label: "Offers" }
];

const OTHER_COLUMNS = [
  {
    title: "Support",
    links: [
      { to: "/track", label: "Track an order" },
      { to: "/returns", label: "Returns" },
      { to: "/contact", label: "Contact us" }
    ]
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "Our story" },
      { to: "/account", label: "Account" },
      { to: "/privacy", label: "Privacy policy" },
      { to: "/terms", label: "Terms & conditions" }
    ]
  }
];

export function Footer() {
  const { data: config } = useStoreConfig();
  const siteName = config?.storeName || "Latif General Store";
  const whatsappNumber = config?.whatsappNumber?.replace(/[^\d]/g, "");
  const { data: categories } = useCategories();
  const shopLinks = [
    ...(categories ?? []).map((c) => ({ to: `/category/${c.slug}`, label: c.name })),
    ...STATIC_SHOP_LINKS
  ];
  const columns = [{ title: "Shop", links: shopLinks }, ...OTHER_COLUMNS];

  return (
    <footer className="border-t border-border bg-canvas">
      <div className="mx-auto max-w-7xl px-6 py-20 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <BrandLogo variant="full" className="h-24 w-auto mb-4 -ml-1" />
          <p className="text-sm text-ink-muted max-w-xs">
            Considered essentials for everyday life — toys and hair accessories, made to last.
          </p>
        </div>
        {columns.map((col) => (
          <div key={col.title}>
            <p className="text-sm font-semibold mb-4">{col.title}</p>
            <ul className="flex flex-col gap-3">
              {col.links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-ink-muted hover:text-ink transition-colors duration-300">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5 py-3.5">
            <Banknote size={14} className="text-accent" aria-hidden />
            Cash on delivery
          </span>
          {whatsappNumber && (
            <a
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 py-3.5 hover:text-ink transition-colors duration-300 active:scale-95"
            >
              <MessageCircle size={14} className="text-accent" aria-hidden />
              WhatsApp support
            </a>
          )}
        </div>
      </div>
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-6 text-xs text-ink-muted flex items-center justify-between">
          <span>© {new Date().getFullYear()} {siteName}. All rights reserved.</span>
          <span>Made with care.</span>
        </div>
      </div>
    </footer>
  );
}
